import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
  Checkbox,
  FormControlLabel,
  List,
  ListItem,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  PersonAdd as PersonAddIcon,
  Search as SearchIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { API_BASE_URL } from '../../config/api';

interface Manager {
  user_id: string;
  email: string;
  first_name: string;
  last_name: string;
  is_active: boolean;
  business_count: number;
  businesses: string;
  created_at: string;
  last_login_at: string;
}

interface Business {
  business_id: string;
  business_name: string;
  business_email: string;
}

const MultiBusinessManagement: React.FC = () => {
  const [managers, setManagers] = useState<Manager[]>([]);
  const [businesses, setBusinesses] = useState<Business[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [manageBusinessesDialogOpen, setManageBusinessesDialogOpen] = useState(false);
  const [removeManagerDialogOpen, setRemoveManagerDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<Manager | null>(null);
  const [selectedBusinessIds, setSelectedBusinessIds] = useState<string[]>([]);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [userSearchTerm, setUserSearchTerm] = useState<string>('');
  const [reassignBusinessId, setReassignBusinessId] = useState<string>('');
  const [reassignRoleId, setReassignRoleId] = useState<string>('');
  const [roles, setRoles] = useState<any[]>([]);
  
  // Form states
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const [managersRes, businessesRes, usersRes, rolesRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/multi-business-manager/managers`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/businesses`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/multi-business-manager/all-users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API_BASE_URL}/roles`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setManagers(managersRes.data.managers || []);
      setBusinesses(businessesRes.data || []);
      // The endpoint already filters out multi-business managers, so use all users
      setAllUsers(usersRes.data.users || []);
      // Filter out multi-business manager role from the list
      setRoles((rolesRes.data || []).filter((role: any) => 
        !role.isMultiBusinessManager && !role.is_multi_business_manager
      ));
    } catch (err: any) {
      console.error('Error fetching data:', err);
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateManager = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      // First, get the Multi Business Manager role ID
      const rolesRes = await axios.get(`${API_BASE_URL}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const multiBusinessRole = rolesRes.data.find((role: any) => 
        role.roleName === 'Multi Business Manager' || 
        role.role_name === 'Multi Business Manager' || 
        role.isMultiBusinessManager === true ||
        role.is_multi_business_manager === true
      );
      
      if (!multiBusinessRole) {
        setError('Multi Business Manager role not found. Please run the database migration first.');
        return;
      }
      
      // Create the user with Multi Business Manager role
      const userData = {
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        username: formData.email.split('@')[0],
        roleId: multiBusinessRole.roleId || multiBusinessRole.role_id,
        businessId: null, // Multi-business managers don't belong to a single business
        isActive: true,
      };
      
      await axios.post(`${API_BASE_URL}/users`, userData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSuccess('Multi Business Manager created successfully!');
      setCreateDialogOpen(false);
      setFormData({ email: '', password: '', firstName: '', lastName: '' });
      fetchData();
    } catch (err: any) {
      console.error('Error creating manager:', err);
      setError(err.response?.data?.error || 'Failed to create manager');
    }
  };

  const handleConvertUser = async () => {
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      // Get the Multi Business Manager role ID
      const rolesRes = await axios.get(`${API_BASE_URL}/roles`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const multiBusinessRole = rolesRes.data.find((role: any) => 
        role.roleName === 'Multi Business Manager' || 
        role.role_name === 'Multi Business Manager' || 
        role.isMultiBusinessManager === true ||
        role.is_multi_business_manager === true
      );
      
      if (!multiBusinessRole) {
        setError('Multi Business Manager role not found. Please run the database migration first.');
        return;
      }
      
      // Update the user's role
      await axios.put(
        `${API_BASE_URL}/users/${selectedUserId}`,
        {
          roleId: multiBusinessRole.roleId || multiBusinessRole.role_id,
          businessId: null, // Remove business association
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('User converted to Multi Business Manager successfully!');
      setConvertDialogOpen(false);
      setSelectedUserId('');
      fetchData();
    } catch (err: any) {
      console.error('Error converting user:', err);
      setError(err.response?.data?.error || 'Failed to convert user');
    }
  };

  const handleOpenManageBusinesses = async (manager: Manager) => {
    try {
      setSelectedManager(manager);
      
      // Fetch the businesses this manager has access to
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_BASE_URL}/multi-business-manager/managers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Find current manager's businesses
      const currentManager = response.data.managers.find(
        (m: Manager) => m.user_id === manager.user_id
      );
      
      if (currentManager && currentManager.businesses) {
        // Parse business names and match with IDs
        const businessNames = currentManager.businesses.split(', ');
        const businessIds = businesses
          .filter(b => businessNames.includes(b.business_name))
          .map(b => b.business_id);
        setSelectedBusinessIds(businessIds);
      } else {
        setSelectedBusinessIds([]);
      }
      
      setManageBusinessesDialogOpen(true);
    } catch (err) {
      console.error('Error fetching manager businesses:', err);
      setSelectedBusinessIds([]);
      setManageBusinessesDialogOpen(true);
    }
  };

  const handleToggleBusiness = (businessId: string) => {
    setSelectedBusinessIds(prev => {
      if (prev.includes(businessId)) {
        return prev.filter(id => id !== businessId);
      } else {
        return [...prev, businessId];
      }
    });
  };

  const handleSaveBusinesses = async () => {
    if (!selectedManager) return;
    
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      // Get current businesses
      const currentManager = managers.find(m => m.user_id === selectedManager.user_id);
      const currentBusinessNames = currentManager?.businesses?.split(', ') || [];
      const currentBusinessIds = businesses
        .filter(b => currentBusinessNames.includes(b.business_name))
        .map(b => b.business_id);
      
      // Determine which businesses to add and remove
      const toAdd = selectedBusinessIds.filter(id => !currentBusinessIds.includes(id));
      const toRemove = currentBusinessIds.filter(id => !selectedBusinessIds.includes(id));
      
      // Add new businesses
      for (const businessId of toAdd) {
        await axios.post(
          `${API_BASE_URL}/multi-business-manager/add-business`,
          {
            managerId: selectedManager.user_id,
            businessId: businessId
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      // Remove businesses
      for (const businessId of toRemove) {
        await axios.delete(
          `${API_BASE_URL}/multi-business-manager/remove-business`,
          {
            headers: { Authorization: `Bearer ${token}` },
            data: {
              managerId: selectedManager.user_id,
              businessId: businessId
            }
          }
        );
      }
      
      setSuccess('Business assignments updated successfully!');
      setManageBusinessesDialogOpen(false);
      setSelectedManager(null);
      setSelectedBusinessIds([]);
      fetchData();
    } catch (err: any) {
      console.error('Error updating business assignments:', err);
      setError(err.response?.data?.error || 'Failed to update business assignments');
    }
  };

  const handleOpenRemoveManager = (manager: Manager) => {
    setSelectedManager(manager);
    setReassignBusinessId('');
    setReassignRoleId('');
    setRemoveManagerDialogOpen(true);
  };

  const handleRemoveManager = async () => {
    if (!selectedManager || !reassignBusinessId || !reassignRoleId) {
      setError('Please select both a business and a role');
      return;
    }
    
    try {
      setError(null);
      const token = localStorage.getItem('token');
      
      await axios.post(
        `${API_BASE_URL}/multi-business-manager/remove-manager`,
        {
          managerId: selectedManager.user_id,
          businessId: reassignBusinessId,
          roleId: reassignRoleId
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess('Multi Business Manager removed and reassigned successfully!');
      setRemoveManagerDialogOpen(false);
      setSelectedManager(null);
      setReassignBusinessId('');
      setReassignRoleId('');
      fetchData();
    } catch (err: any) {
      console.error('Error removing manager:', err);
      setError(err.response?.data?.error || 'Failed to remove and reassign manager');
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 700 }}>
            Multi-Business Managers
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage users who can access multiple businesses
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<PersonAddIcon />}
            onClick={() => setConvertDialogOpen(true)}
          >
            Convert Existing User
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create New Manager
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Name</strong></TableCell>
                  <TableCell><strong>Email</strong></TableCell>
                  <TableCell><strong>Businesses</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Last Login</strong></TableCell>
                  <TableCell align="right"><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {managers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <Typography color="text.secondary">
                        No multi-business managers found. Create one to get started.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  managers.map((manager) => (
                    <TableRow key={manager.user_id}>
                      <TableCell>
                        {manager.first_name} {manager.last_name}
                      </TableCell>
                      <TableCell>{manager.email}</TableCell>
                      <TableCell>
                        <Chip
                          icon={<BusinessIcon />}
                          label={`${manager.business_count} ${manager.business_count === 1 ? 'business' : 'businesses'}`}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={manager.is_active ? 'Active' : 'Inactive'}
                          color={manager.is_active ? 'success' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {manager.last_login_at
                          ? new Date(manager.last_login_at).toLocaleDateString()
                          : 'Never'}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenManageBusinesses(manager)}
                            title="Manage Businesses"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleOpenRemoveManager(manager)}
                            title="Remove Manager & Reassign"
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Create Manager Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Multi-Business Manager</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="First Name"
              fullWidth
              value={formData.firstName}
              onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
            />
            <TextField
              label="Last Name"
              fullWidth
              value={formData.lastName}
              onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            />
            <TextField
              label="Password"
              type="password"
              fullWidth
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            />
            <Alert severity="info">
              After creating the manager, you can assign businesses to them using the "Manage Businesses" button.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateManager}
            disabled={!formData.email || !formData.password || !formData.firstName || !formData.lastName}
          >
            Create Manager
          </Button>
        </DialogActions>
      </Dialog>

      {/* Convert Existing User Dialog */}
      <Dialog 
        open={convertDialogOpen} 
        onClose={() => {
          setConvertDialogOpen(false);
          setUserSearchTerm('');
          setSelectedUserId('');
        }} 
        maxWidth="sm" 
        fullWidth
      >
        <DialogTitle>Convert Existing User to Multi-Business Manager</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Search users"
              placeholder="Search by name, email, or role..."
              value={userSearchTerm}
              onChange={(e) => setUserSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 2 }}
            />
            <FormControl fullWidth>
              <InputLabel>Select User</InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label="Select User"
                MenuProps={{
                  PaperProps: {
                    style: {
                      maxHeight: 300,
                      width: 'auto',
                    },
                  },
                }}
              >
                <MenuItem value="">
                  <em>Choose a user...</em>
                </MenuItem>
                {allUsers
                  .filter((user) => {
                    if (!userSearchTerm) return true;
                    const searchLower = userSearchTerm.toLowerCase();
                    return (
                      user.first_name?.toLowerCase().includes(searchLower) ||
                      user.last_name?.toLowerCase().includes(searchLower) ||
                      user.email?.toLowerCase().includes(searchLower) ||
                      user.role_name?.toLowerCase().includes(searchLower) ||
                      `${user.first_name} ${user.last_name}`.toLowerCase().includes(searchLower)
                    );
                  })
                  .map((user) => (
                    <MenuItem key={user.user_id} value={user.user_id}>
                      {user.first_name} {user.last_name} ({user.email}) - {user.role_name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This will change the user's role to Multi Business Manager and remove their current business association. They will need to be assigned businesses to manage.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleConvertUser}
            disabled={!selectedUserId}
          >
            Convert User
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Businesses Dialog */}
      <Dialog
        open={manageBusinessesDialogOpen}
        onClose={() => setManageBusinessesDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Manage Businesses for {selectedManager?.first_name} {selectedManager?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select the businesses this manager can access:
            </Typography>
            <List>
              {businesses.map((business) => (
                <ListItem key={business.business_id} dense>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={selectedBusinessIds.includes(business.business_id)}
                        onChange={() => handleToggleBusiness(business.business_id)}
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="body1">{business.business_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {business.business_email}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setManageBusinessesDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveBusinesses}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Manager & Reassign Dialog */}
      <Dialog
        open={removeManagerDialogOpen}
        onClose={() => {
          setRemoveManagerDialogOpen(false);
          setSelectedManager(null);
          setReassignBusinessId('');
          setReassignRoleId('');
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Remove Multi-Business Manager: {selectedManager?.first_name} {selectedManager?.last_name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Alert severity="warning">
              This will remove this user from the Multi Business Manager role and reassign them to a single business with a regular role. All their multi-business access will be revoked.
            </Alert>
            
            <FormControl fullWidth required>
              <InputLabel>Assign to Business</InputLabel>
              <Select
                value={reassignBusinessId}
                onChange={(e) => setReassignBusinessId(e.target.value)}
                label="Assign to Business"
              >
                <MenuItem value="">
                  <em>Select a business...</em>
                </MenuItem>
                {businesses.map((business) => (
                  <MenuItem key={business.business_id} value={business.business_id}>
                    {business.business_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth required>
              <InputLabel>Assign Role</InputLabel>
              <Select
                value={reassignRoleId}
                onChange={(e) => setReassignRoleId(e.target.value)}
                label="Assign Role"
              >
                <MenuItem value="">
                  <em>Select a role...</em>
                </MenuItem>
                {roles.map((role) => (
                  <MenuItem key={role.roleId || role.role_id} value={role.roleId || role.role_id}>
                    {role.roleName || role.role_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => {
            setRemoveManagerDialogOpen(false);
            setSelectedManager(null);
            setReassignBusinessId('');
            setReassignRoleId('');
          }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleRemoveManager}
            disabled={!reassignBusinessId || !reassignRoleId}
          >
            Remove & Reassign
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default MultiBusinessManagement;

